import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { ReactNode, useId } from 'react';
import { Control, UseFormSetValue, useFormState, useWatch } from 'react-hook-form';

import DateInput from '~components/FormFields/v2/DateInput';
import Label from '~components/FormFields/v2/Label';
import NumberInput from '~components/FormFields/v2/NumberInput';
import TextInput from '~components/FormFields/v2/TextInput';
import TimeInput from '~components/FormFields/v2/TimeInput';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { Timesheet, TimesheetMetadata } from '~hooks/useTimesheets/models';
import { Nullable } from '~types/Nullable';
import { mergeDateTime, minutesToDecimalHours } from '~utils/dateTime';

import TimesheetMetadataSummary from './TimesheetMetadataSummary';
import { TimesheetFormValues } from './timesheetSchema';

const emDash = '-';
// Same sizes as the ticket drawer's read view, so the two drawers read alike.
const STAT_LABEL_FONT_SIZE = '10px';
const STAT_VALUE_FONT_SIZE = '16px';
const STAT_SUBTEXT_FONT_SIZE = '11px';
const SUPPORTING_LABEL_FONT_SIZE = '10px';
const SUPPORTING_VALUE_FONT_SIZE = '12px';
const SECTION_TITLE_FONT_SIZE = '18px';
const SECTION_CAPTION_FONT_SIZE = '12px';
const LONG_VALUE_WRAP = { overflowWrap: 'anywhere' } as const;
const TIMESHEET_NUMBER_FONT_SIZE = '28px';
const HOURS_PRECISION = 2;

const formatTime = (value: Nullable<Dayjs>) =>
  value ? dayjs.tz(value).format('h:mm A') : emDash;
const formatDate = (value: Nullable<Dayjs>) =>
  value ? dayjs.tz(value).format('MMM D, YYYY') : emDash;
const formatDateTime = (value: Nullable<Dayjs>) =>
  value ? `${formatTime(value)} · ${formatDate(value)}` : emDash;
const formatHours = (minutes: Nullable<number>) => {
  const hours = minutesToDecimalHours(minutes);

  return isNil(hours) ? emDash : `${hours.toFixed(HOURS_PRECISION)}h`;
};
const formatBreak = (minutes: Nullable<number>) =>
  isNil(minutes) ? emDash : t('approvals.timesheets.minutes', { minutes });

function Subtext({ children }: { children: string }) {
  return (
    <Typography
      sx={(theme) => ({
        color: theme.brandV2.colors.treadGray2,
        fontSize: STAT_SUBTEXT_FONT_SIZE,
        mt: 0.5,
        ...LONG_VALUE_WRAP,
      })}
    >
      {children}
    </Typography>
  );
}

function ScannedValue({ value }: { value: string | null }) {
  if (!value) {
    return null;
  }

  return <Subtext>{t('approvals.timesheets.scanned', { value })}</Subtext>;
}

function Stat({ children, title }: { children: ReactNode; title: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontSize: STAT_LABEL_FONT_SIZE, fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function StatValue({ children }: { children: string }) {
  const displayValue = children.trim() !== '' ? children : emDash;

  return (
    <Typography sx={{ fontSize: STAT_VALUE_FONT_SIZE, ...LONG_VALUE_WRAP }}>
      {displayValue}
    </Typography>
  );
}

function SupportingStat({ title, value }: { title: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray2,
          fontSize: SUPPORTING_LABEL_FONT_SIZE,
        })}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: SUPPORTING_VALUE_FONT_SIZE, ...LONG_VALUE_WRAP }}>
        {value.trim() !== '' ? value : emDash}
      </Typography>
    </Box>
  );
}

function Section({
  caption,
  children,
  title,
}: {
  caption?: string;
  children: ReactNode;
  title: string;
}) {
  const titleId = useId();

  return (
    <Box aria-labelledby={titleId} component="section">
      <Typography
        id={titleId}
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: SECTION_TITLE_FONT_SIZE,
          fontWeight: 600,
          mb: caption ? 0.5 : 2,
        })}
      >
        {title}
      </Typography>
      {caption && (
        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray2,
            fontSize: SECTION_CAPTION_FONT_SIZE,
            mb: 2,
          })}
        >
          {caption}
        </Typography>
      )}
      <Box
        sx={(theme) => ({
          columnGap: 2,
          display: 'grid',
          gridTemplateColumns: `repeat(2, calc(50% - ${theme.spacing(1)}))`,
          rowGap: 3,
        })}
      >
        {children}
      </Box>
    </Box>
  );
}

function TotalHoursLabel({
  control,
  setValue,
}: {
  control: Control<TimesheetFormValues>;
  setValue: UseFormSetValue<TimesheetFormValues>;
}) {
  const [startedAtTime, startedAtDate, endedAtTime, endedAtDate, totalHours] = useWatch({
    control,
    name: ['startedAtTime', 'startedAtDate', 'endedAtTime', 'endedAtDate', 'totalHours'],
  });
  const { dirtyFields } = useFormState({ control });

  const calculatedTotalHours =
    startedAtTime?.isValid() &&
    startedAtDate?.isValid() &&
    endedAtTime?.isValid() &&
    endedAtDate?.isValid()
      ? Number(
          dayjs(mergeDateTime(endedAtDate, endedAtTime))
            .diff(mergeDateTime(startedAtDate, startedAtTime), 'hour', true)
            .toFixed(HOURS_PRECISION),
        )
      : null;
  const startOrEndChanged = Boolean(
    dirtyFields.startedAtTime ||
    dirtyFields.startedAtDate ||
    dirtyFields.endedAtTime ||
    dirtyFields.endedAtDate,
  );
  const shouldSuggestCalculatedTotal =
    startOrEndChanged &&
    !isNil(calculatedTotalHours) &&
    calculatedTotalHours > 0 &&
    calculatedTotalHours !== Number(totalHours);

  return (
    <Box
      sx={{
        alignItems: 'baseline',
        display: 'flex',
        gap: 1,
        justifyContent: 'space-between',
      }}
    >
      <Label>{t('approvals.timesheets.total_hours')}</Label>
      {shouldSuggestCalculatedTotal && (
        <SmallButton
          color="info"
          onClick={() =>
            setValue('totalHours', calculatedTotalHours, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          variant="text"
        >
          {t('approvals.timesheets.use_calculated_hours', {
            hours: calculatedTotalHours.toFixed(HOURS_PRECISION),
          })}
        </SmallButton>
      )}
    </Box>
  );
}

interface TimesheetDetailsViewProps {
  control: Control<TimesheetFormValues>;
  isEditing: boolean;
  isLoadingMetadata?: boolean;
  metadata?: TimesheetMetadata;
  setValue: UseFormSetValue<TimesheetFormValues>;
  timesheet: Timesheet;
}

export default function TimesheetDetailsView({
  control,
  isEditing,
  isLoadingMetadata,
  metadata,
  setValue,
  timesheet,
}: TimesheetDetailsViewProps) {
  const timesheetForTitleId = useId();
  const linkedDriver = timesheet.driverDay?.driver;
  const linkedDriverName = linkedDriver
    ? [linkedDriver.firstName, linkedDriver.lastName]
        .filter((namePart) => Boolean(namePart))
        .join(' ')
    : emDash;

  const scannedNumber = timesheet.extractedNumber ?? null;
  const scannedStart = timesheet.extractedStartAt
    ? formatDateTime(timesheet.extractedStartAt)
    : null;
  const scannedEnd = timesheet.extractedEndAt
    ? formatDateTime(timesheet.extractedEndAt)
    : null;
  const scannedTotal = isNil(timesheet.extractedTotalMinutes)
    ? null
    : formatHours(timesheet.extractedTotalMinutes);
  const scannedBreak = isNil(timesheet.extractedBreakMinutes)
    ? null
    : formatBreak(timesheet.extractedBreakMinutes);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {isEditing ? (
        <>
          <Section title={t('approvals.timesheets.timesheet_details')}>
            <Box sx={{ gridColumn: 'span 2' }}>
              <TextInput
                control={control}
                // Clicking Edit unmounts the Edit button, so focus would otherwise fall out of
                // the drawer and Escape would stop reaching it.
                slotProps={{
                  htmlInput: {
                    'aria-label': t('approvals.timesheets.timesheet_number_long'),
                    autoFocus: true,
                  },
                }}
                label={t('approvals.timesheets.timesheet_number_long')}
                name="timesheetNumber"
              />
              <ScannedValue value={scannedNumber} />
            </Box>
          </Section>

          <Section
            caption={t('approvals.timesheets.edits_stay_on_timesheet')}
            title={t('approvals.timesheets.times')}
          >
            <Box>
              <TimeInput
                control={control}
                label={t('approvals.timesheets.start_time')}
                name="startedAtTime"
              />
              <ScannedValue value={scannedStart} />
            </Box>
            <DateInput
              control={control}
              label={t('approvals.timesheets.start_date')}
              name="startedAtDate"
            />
            <Box>
              <TimeInput
                control={control}
                label={t('approvals.timesheets.end_time')}
                name="endedAtTime"
              />
              <ScannedValue value={scannedEnd} />
            </Box>
            <DateInput
              control={control}
              label={t('approvals.timesheets.end_date')}
              name="endedAtDate"
            />
            <Box>
              <NumberInput
                control={control}
                slotProps={{
                  htmlInput: { 'aria-label': t('approvals.timesheets.total_hours') },
                }}
                label={<TotalHoursLabel control={control} setValue={setValue} />}
                name="totalHours"
              />
              <ScannedValue value={scannedTotal} />
            </Box>
            <Box>
              <NumberInput
                control={control}
                slotProps={{
                  htmlInput: {
                    'aria-label': t('approvals.timesheets.break_in_minutes'),
                  },
                }}
                label={t('approvals.timesheets.break_in_minutes')}
                name="breakMinutes"
              />
              <ScannedValue value={scannedBreak} />
            </Box>
          </Section>
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: TIMESHEET_NUMBER_FONT_SIZE,
              fontWeight: 600,
              ...LONG_VALUE_WRAP,
            }}
          >
            {timesheet.timesheetNumber || emDash}
          </Typography>

          <Section title={t('approvals.timesheets.times')}>
            <Stat title={t('approvals.timesheets.start')}>
              <StatValue>{formatDateTime(timesheet.startedAt)}</StatValue>
              <ScannedValue value={scannedStart} />
            </Stat>
            <Stat title={t('approvals.timesheets.end')}>
              <StatValue>{formatDateTime(timesheet.endedAt)}</StatValue>
              <ScannedValue value={scannedEnd} />
            </Stat>
            <Stat title={t('approvals.timesheets.total_hours')}>
              <StatValue>{formatHours(timesheet.totalMinutes)}</StatValue>
              <ScannedValue value={scannedTotal} />
            </Stat>
            <Stat title={t('approvals.timesheets.break_minutes')}>
              <StatValue>{formatBreak(timesheet.breakMinutes)}</StatValue>
              <ScannedValue value={scannedBreak} />
            </Stat>
          </Section>
        </>
      )}

      {(timesheet.job || timesheet.driverDay) && (
        <>
          <Divider />
          <Box aria-labelledby={timesheetForTitleId} component="section">
            <Typography
              id={timesheetForTitleId}
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '12px',
                fontWeight: 600,
                mb: 1,
              })}
            >
              {t('approvals.timesheets.timesheet_for')}
            </Typography>
            <Box
              sx={{
                columnGap: 2,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                rowGap: 1,
              }}
            >
              {timesheet.driverDay && (
                <Box sx={{ gridColumn: 'span 2' }}>
                  <SupportingStat
                    title={t('form_fields.driver')}
                    value={linkedDriverName}
                  />
                </Box>
              )}
              {timesheet.job && (
                <>
                  <SupportingStat
                    title={t('approvals.timesheets.linked_job')}
                    value={timesheet.job.jobId}
                  />
                  <SupportingStat
                    title={t('approvals.timesheets.job_start')}
                    value={formatDate(timesheet.job.jobStartAt)}
                  />
                </>
              )}
              {timesheet.driverDay && (
                <>
                  <SupportingStat
                    title={t('approvals.timesheets.linked_shift')}
                    value={timesheet.driverDay.driverDayId}
                  />
                  <SupportingStat
                    title={t('approvals.timesheets.shift_date')}
                    value={formatDate(timesheet.driverDay.driverDayDate)}
                  />
                </>
              )}
            </Box>
          </Box>
        </>
      )}

      <Divider />
      <TimesheetMetadataSummary
        confidenceScore={timesheet.aiConfidenceScore}
        isLoading={isLoadingMetadata}
        metadata={metadata}
      />
    </Box>
  );
}
